/**
 * Thin fetch wrapper.
 *
 *  - Prefixes `/api/v1` if the caller hands us a bare path.
 *  - Injects `Authorization: Bearer <token>` from the in-memory token store.
 *  - Injects `X-Tenant-Slug` when the caller passes a tenant.
 *  - Sends cookies (`credentials: "include"`) so the httpOnly refresh cookie
 *    travels with every request.
 *  - On 401: invokes `refresh()` once, retries the request once. On a second
 *    401 it surfaces an `ApiError` and the route layer will redirect to /login.
 *  - On non-2xx: parses `application/problem+json` (RFC 7807) and throws
 *    `ApiError`. The UI surfaces `problem.detail` (Dutch) verbatim.
 *  - 204 responses resolve to `undefined as T`.
 */

import { getAccessToken } from './auth';
import type { ProblemDetail } from './api.gen';

// ---------------------------------------------------------------------------
// ApiError
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  public readonly status: number;
  public readonly problem: ProblemDetail;

  constructor(status: number, problem: ProblemDetail) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

// ---------------------------------------------------------------------------
// apiRequest
// ---------------------------------------------------------------------------

export type ApiRequestInit = Omit<RequestInit, 'body'> & {
  /** Tenant slug to forward as `X-Tenant-Slug`, or "all" for cross-tenant. */
  tenant?: string | 'all';
  /** Pre-serialised body (JSON string, FormData, etc). */
  body?: BodyInit | null;
  /**
   * Skip the auto-refresh-on-401 dance. Used internally by /auth/login,
   * /auth/refresh and /auth/logout to avoid infinite recursion.
   */
  skipAuthRefresh?: boolean;
};

const API_PREFIX = '/api/v1';

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith(API_PREFIX)) return path;
  if (path.startsWith('/')) return `${API_PREFIX}${path}`;
  return `${API_PREFIX}/${path}`;
}

function buildHeaders(init: ApiRequestInit): Headers {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const method = (init.method ?? 'GET').toUpperCase();
  const hasBody = init.body != null;
  const bodyIsFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (method !== 'GET' && hasBody && !bodyIsFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getAccessToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (init.tenant && !headers.has('X-Tenant-Slug')) {
    headers.set('X-Tenant-Slug', init.tenant);
  }

  return headers;
}

async function parseProblem(response: Response): Promise<ProblemDetail> {
  const fallback: ProblemDetail = {
    type: 'about:blank',
    title: `HTTP ${response.status}`,
    status: response.status,
  };
  // Try JSON first regardless of Content-Type — some proxies strip it.
  try {
    const text = await response.text();
    if (!text) return fallback;
    const parsed = JSON.parse(text) as Partial<ProblemDetail>;
    return {
      type: parsed.type ?? fallback.type,
      title: parsed.title ?? fallback.title,
      status: parsed.status ?? response.status,
      detail: parsed.detail,
      instance: parsed.instance,
      ...parsed,
    };
  } catch {
    return fallback;
  }
}

async function parseSuccess<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  // Try JSON; if it isn't JSON, surface the raw text.
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export async function apiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const url = buildUrl(path);

  const doFetch = async (): Promise<Response> => {
    return fetch(url, {
      ...init,
      headers: buildHeaders(init),
      credentials: 'include',
    });
  };

  let response = await doFetch();

  if (response.status === 401 && !init.skipAuthRefresh) {
    // Dynamic import to avoid a hard cycle (auth.ts imports apiRequest).
    const { refresh } = await import('./auth');
    const refreshed = await refresh();
    if (refreshed) {
      response = await doFetch();
    }
  }

  if (!response.ok) {
    const problem = await parseProblem(response);
    throw new ApiError(response.status, problem);
  }

  return parseSuccess<T>(response);
}

// ---------------------------------------------------------------------------
// Typed verb helpers
// ---------------------------------------------------------------------------

type VerbOptions = Omit<ApiRequestInit, 'method' | 'body'>;

export function apiGet<T>(path: string, opts: VerbOptions = {}): Promise<T> {
  return apiRequest<T>(path, { ...opts, method: 'GET' });
}

export function apiPost<T>(path: string, body?: unknown, opts: VerbOptions = {}): Promise<T> {
  return apiRequest<T>(path, {
    ...opts,
    method: 'POST',
    body: body === undefined ? null : JSON.stringify(body),
  });
}

export function apiPatch<T>(path: string, body?: unknown, opts: VerbOptions = {}): Promise<T> {
  return apiRequest<T>(path, {
    ...opts,
    method: 'PATCH',
    body: body === undefined ? null : JSON.stringify(body),
  });
}

export function apiDelete<T>(path: string, opts: VerbOptions = {}): Promise<T> {
  return apiRequest<T>(path, { ...opts, method: 'DELETE' });
}
